class CartDiscount extends HTMLElement {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  constructor() {
    super();

    this.cartDrawer = document.querySelector('cart-drawer');
    this.cartItems = document.querySelector('cart-items');

    this.attachEventListeners();
  }

  attachEventListeners() {
    const form = this.querySelector('form');
    if (form) {
      form.removeEventListener('submit', this.applyDiscount.bind(this));
      form.addEventListener('submit', this.applyDiscount.bind(this));
    }

    const discountPills = this.querySelectorAll('.cart-discount__pill-remove');

    if (discountPills.length) {
      for (const pill of discountPills) {
        pill.addEventListener('click', this.removeDiscount.bind(this));
      }
    }
  }

  get refs() {
    const [cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping] = this.requiredRefs.map(
      (refName) => this.querySelector(`[ref="${refName}"]`)
    );
    return { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping };
  }

  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      this.toggleLoading(true);

      const sections = this.cartDrawer
        ? [this.dataset.sectionId]
        : [document.getElementById('main-cart-items').dataset.id, this.dataset.sectionId];

      const body = JSON.stringify({
        discount: [...existingDiscounts, discountCodeValue].join(','),
        sections: sections.join(','),
        sections_url: window.location.pathname
      });

      const response = await fetch(routes.cart_update_url,
        { ...fetchConfig(), ...{ body }, signal: abortController.signal }
      );

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      if (this.cartDrawer && this.cartDrawer.renderContents) {
        this.cartDrawer.renderContents(data);
      } else if (this.cartItems) {
        const sections = [
          { id: 'main-cart-items', section: document.getElementById('main-cart-items').dataset.id, selector: '#main-cart-items .js-contents' },
          { id: 'main-cart-footer', section: document.getElementById('main-cart-items').dataset.id, selector: '#main-cart-footer .js-contents' }
        ];

        sections.forEach((section) => {
          const element = document.getElementById(section.id);
          if (element && data.sections[section.section]) {
            const targetContents = element.querySelector('.js-contents');
            if (targetContents) {
              targetContents.innerHTML = this.cartItems.getSectionInnerHTML(data.sections[section.section], section.selector);
            }
          }
        });

        if (data.sections[this.dataset.sectionId]) {
          const html = new DOMParser().parseFromString(data.sections[this.dataset.sectionId], 'text/html');
          const sourceDiscountComponent = html.querySelector('cart-discount-component');
          if (sourceDiscountComponent) {
            this.innerHTML = sourceDiscountComponent.innerHTML;
            this.attachEventListeners();
          }
        }

        const footerCartBubble = document.getElementById('cart-icon-bubble-footer');
        if (footerCartBubble) {
          const countSpan = footerCartBubble.querySelector('span[aria-hidden="true"]');
          if (countSpan) {
            countSpan.textContent = data.item_count;
          }
        }

        this.cartItems.dispatchCartUpdatedEvent(data);
      }
    } catch (error) {
    } finally {
      this.toggleLoading(false);
      this.#activeFetch = null;
    }
  };

  toggleLoading(loading, target = this) {
    target
      .querySelectorAll('.loading-spinner')
      ?.forEach((spinner) => spinner.classList.toggle('hidden', !loading));
  }

  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      this.toggleLoading(true);

      const sections = this.cartDrawer
        ? [this.dataset.sectionId]
        : [document.getElementById('main-cart-items').dataset.id, this.dataset.sectionId];

      const body = JSON.stringify({
        discount: existingDiscounts.join(','),
        sections: sections.join(','),
        sections_url: window.location.pathname
      });
      const response = await fetch(routes.cart_update_url, { ...fetchConfig(), ...{ body }, signal: abortController.signal });

      const data = await response.json();

      if (this.cartDrawer && this.cartDrawer.renderContents) {
        this.cartDrawer.renderContents(data);
      } else if (this.cartItems) {
        const sections = [
          { id: 'main-cart-items', section: document.getElementById('main-cart-items').dataset.id, selector: '#main-cart-items .js-contents' },
          { id: 'main-cart-footer', section: document.getElementById('main-cart-items').dataset.id, selector: '#main-cart-footer .js-contents' }
        ];

        sections.forEach((section) => {
          const element = document.getElementById(section.id);
          if (element && data.sections[section.section]) {
            const targetContents = element.querySelector('.js-contents');
            if (targetContents) {
              targetContents.innerHTML = this.cartItems.getSectionInnerHTML(data.sections[section.section], section.selector);
            }
          }
        });

        if (data.sections[this.dataset.sectionId]) {
          const html = new DOMParser().parseFromString(data.sections[this.dataset.sectionId], 'text/html');
          const sourceDiscountComponent = html.querySelector('cart-discount-component');
          if (sourceDiscountComponent) {
            this.innerHTML = sourceDiscountComponent.innerHTML;
            this.attachEventListeners();
          }
        }

        const footerCartBubble = document.getElementById('cart-icon-bubble-footer');
        if (footerCartBubble) {
          const countSpan = footerCartBubble.querySelector('span[aria-hidden="true"]');
          if (countSpan) {
            countSpan.textContent = data.item_count;
          }
        }

        this.cartItems.dispatchCartUpdatedEvent(data);
      }
    } catch (error) {
    } finally {
      this.toggleLoading(false);
      this.#activeFetch = null;
    }
  };

  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  #existingDiscounts() {
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}